import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ArrowUpRight, Clapperboard, Layers3, Loader2, Sparkles } from 'lucide-react'
import { appApiBaseUrl, appFetch } from '@/shared/api/app-client'
import type { ApiResponse } from '@/shared/api/app-types'

type PublicShare = {
  title: string
  captionText: string | null
  coverUrl: string | null
  durationMs: number | null
  videoUrl: string
}

export const Route = createFileRoute('/s/$shareKey')({
  component: PublicSharePage,
})

function PublicSharePage() {
  const { shareKey } = Route.useParams()
  const [share, setShare] = useState<PublicShare | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void appFetch<ApiResponse<PublicShare>>(`/api/share-links/${shareKey}`)
      .then((response) => { if (!cancelled) setShare(response.data) })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : '此分享链接不可用。')
      })
    return () => { cancelled = true }
  }, [shareKey])

  if (error) return <ShareUnavailable message={error} />
  if (!share) {
    return <main className="flex min-h-screen items-center justify-center bg-[#0a0a0b] text-zinc-400"><Loader2 className="size-5 animate-spin" /></main>
  }

  const videoUrl = share.videoUrl.startsWith('http') ? share.videoUrl : `${appApiBaseUrl}${share.videoUrl}`
  const coverUrl = share.coverUrl?.startsWith('/uploads/') ? `${appApiBaseUrl}${share.coverUrl}` : share.coverUrl ?? undefined
  return (
    <main className="min-h-screen overflow-hidden bg-[#0a0a0b] text-zinc-100">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(114,91,255,0.18),transparent_42%),radial-gradient(ellipse_at_84%_40%,rgba(15,163,177,0.10),transparent_34%)]" />
      <section className="relative mx-auto max-w-6xl px-4 py-5 sm:px-6 sm:py-8">
        <header className="mb-8 flex items-center justify-between border-b border-white/10 pb-4">
          <a href="/" className="flex items-center gap-2 text-sm font-semibold tracking-tight text-white">
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Clapperboard className="size-4" /></span>
            JIAN
          </a>
          <a href="/" className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 transition-colors hover:text-white">
            制作你的下一条视频 <ArrowUpRight className="size-3.5" />
          </a>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl shadow-black/30">
            <video className="aspect-video w-full bg-black" controls playsInline poster={coverUrl} preload="metadata">
              <source src={videoUrl} type="video/mp4" />
              您的浏览器不支持视频播放。
            </video>
            <div className="border-t border-white/10 px-5 py-5 sm:px-7 sm:py-6">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">JIAN 分享成片</p>
              <h1 className="mt-2 text-xl font-semibold tracking-tight text-white sm:text-2xl">{share.title}</h1>
              {share.captionText ? <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-400">{share.captionText}</p> : null}
            </div>
          </div>

          <aside className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm lg:sticky lg:top-6">
            <div className="flex size-9 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkles className="size-4" /></div>
            <h2 className="mt-4 text-base font-semibold text-white">一个素材，产出多条成片</h2>
            <p className="mt-2 text-sm leading-6 text-zinc-400">JIAN 将长视频、产品素材和品牌模板，组织为可审核、可编辑、可批量发布的多平台视频版本。</p>
            <div className="mt-5 space-y-3 border-y border-white/10 py-4 text-xs text-zinc-400">
              <div className="flex items-center gap-2"><Layers3 className="size-3.5 text-primary" /> 多平台短视频生产工作流</div>
              <div className="flex items-center gap-2"><Clapperboard className="size-3.5 text-primary" /> AI 自动化与精细编辑控制</div>
            </div>
            <a href="/" className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
              了解 JIAN <ArrowUpRight className="size-4" />
            </a>
          </aside>
        </div>
        <p className="mt-6 text-center text-xs text-zinc-600">此页面仅展示该公开分享成片 · 由 JIAN 安全托管</p>
      </section>
    </main>
  )
}

function ShareUnavailable({ message }: { message: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#0a0a0b] px-5 text-center text-zinc-100">
      <div><Clapperboard className="mx-auto size-8 text-zinc-600" /><h1 className="mt-4 text-lg font-semibold">此分享已不可用</h1><p className="mt-2 text-sm text-zinc-500">{message}</p><a href="/" className="mt-6 inline-flex text-sm text-primary hover:underline">了解 JIAN</a></div>
    </main>
  )
}
